import React, { useState, useEffect } from 'react';
import { IntegrationRequest } from '../../lib/tenant-api';
import Modal from '../Modal';
import type { IntegrationConfigProps } from './types';

export const SalesforceConfig: React.FC<IntegrationConfigProps> = ({
  isOpen,
  onClose,
  integration,
  createIntegrationMutation,
  updateIntegrationMutation
}) => {
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    security_token: '',
    consumer_key: '',
    consumer_secret: '',
    instance_url: '',
    is_enabled: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = !!integration;

  useEffect(() => {
    if (integration) {
      setFormData({
        name: integration.name,
        username: integration.config.username || '',
        password: integration.config.password || '',
        security_token: integration.config.security_token || '',
        instance_url: integration.config.instance_url || '',
        consumer_key: integration.config.consumer_key || '',
        consumer_secret: integration.config.consumer_secret || '',
        is_enabled: integration.is_enabled,
      });
    } else {
      setFormData({
        name: '',
        username: '',
        password: '',
        security_token: '',
        consumer_key: '',
        consumer_secret: '',
        instance_url: '',
        is_enabled: true,
      });
    }
    setErrors({});
  }, [integration, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Integration name is required';
    }

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }
    // if (!formData.security_token.trim()) {
    //   newErrors.security_token = 'Security Token is required';
    // }
    if (!formData.consumer_key.trim()) {
      newErrors.consumer_key = 'Consumer Key is required';
    }
    if (!formData.consumer_secret.trim()) {
      newErrors.consumer_secret = 'Consumer Secret is required';
    }
    if (!formData.instance_url.trim()) {
      newErrors.instance_url = 'Instance URL is required';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const integrationData: IntegrationRequest = {
      integration_type: 'salesforce',
      name: formData.name,
      config: {
        username: formData.username,
        password: formData.password,
        consumer_key: formData.consumer_key,
        consumer_secret: formData.consumer_secret,
        security_token: formData.security_token,
        instance_url: formData.instance_url,
      },
      is_enabled: formData.is_enabled,
    };

    if (isEditing && integration) {
      updateIntegrationMutation.mutate({
        id: integration.id,
        data: integrationData,
      });
    } else {
      createIntegrationMutation.mutate(integrationData);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };

      return newData;
    });

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const isLoading = createIntegrationMutation.isLoading || updateIntegrationMutation.isLoading;
  return <Modal
    isOpen={isOpen}
    onClose={handleClose}
    title={isEditing ? `Edit Salesforce Integration` : `Connect Salesforce`}
  >
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Integration Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
          Integration Name
        </label>
        <input
          type="text"
          id="name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.name ? 'border-red-300' : 'border-gray-300'
            }`}
          placeholder="e.g., My Salesforce Integration"
          disabled={isLoading}
        />
        {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
      </div>

      <div>
        <label htmlFor="instance_url" className="block text-sm font-medium text-gray-700 mb-2">
          Instance URL
        </label>
        <input
          type="text"
          id="instance_url"
          value={formData.instance_url}
          onChange={(e) => handleInputChange('instance_url', e.target.value)}
          className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.instance_url ? 'border-red-300' : 'border-gray-300'
            }`}
          placeholder="e.g., https://example.salesforce.com/services/data/v50.0/"
          disabled={isLoading}
        />
        {errors.instance_url && <p className="mt-1 text-sm text-red-600">{errors.instance_url}</p>}
      </div>
      <div>
        <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
          Username
        </label>
        <input
          type="text"
          id="username"
          value={formData.username}
          onChange={(e) => handleInputChange('username', e.target.value)}
          className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.username ? 'border-red-300' : 'border-gray-300'
            }`}
          disabled={isLoading}
        />
        {errors.username && <p className="mt-1 text-sm text-red-600">{errors.username}</p>}
      </div>
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
          Password
        </label>
        <input
          type="password"
          id="password"
          value={formData.password}
          onChange={(e) => handleInputChange('password', e.target.value)}
          className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.password ? 'border-red-300' : 'border-gray-300'
            }`}
          disabled={isLoading}
        />
        {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
      </div>

      <div>
        <label htmlFor="security_token" className="block text-sm font-medium text-gray-700 mb-2">
          Security Token
        </label>
        <input
          type="text"
          id="security_token"
          value={formData.security_token}
          onChange={(e) => handleInputChange('security_token', e.target.value)}
          className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.security_token ? 'border-red-300' : 'border-gray-300'
            }`}
          disabled={isLoading}
        />
        {errors.security_token && <p className="mt-1 text-sm text-red-600">{errors.security_token}</p>}
      </div>

      <div>
        <label htmlFor="consumer_key" className="block text-sm font-medium text-gray-700 mb-2">
          Consumer Key
        </label>
        <input
          type="text"
          id="consumer_key"
          value={formData.consumer_key}
          onChange={(e) => handleInputChange('consumer_key', e.target.value)}
          className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.security_token ? 'border-red-300' : 'border-gray-300'
            }`}
          disabled={isLoading}
        />
        {errors.consumer_key && <p className="mt-1 text-sm text-red-600">{errors.consumer_key}</p>}
      </div>

      <div>
        <label htmlFor="consumer_secret" className="block text-sm font-medium text-gray-700 mb-2">
          Consumer Secret
        </label>
        <input
          type="text"
          id="consumer_secret"
          value={formData.consumer_secret}
          onChange={(e) => handleInputChange('consumer_secret', e.target.value)}
          className={`w-full px-3 py-2 border  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.security_token ? 'border-red-300' : 'border-gray-300'
            }`}
          disabled={isLoading}
        />
        {errors.consumer_secret && <p className="mt-1 text-sm text-red-600">{errors.consumer_secret}</p>}
      </div>


      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={handleClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300  hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent  hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {isEditing ? 'Updating...' : 'Connecting...'}
            </>
          ) : (
            isEditing ? 'Update Integration' : 'Connect Salesforce'
          )}
        </button>
      </div>
    </form>
  </Modal>

}
